// A dumb wrapper for the Facebook API.
// Adds Promises so we can just use async/await like grownups.
// All the logic for login should go in src/client/vanilla/login.js

// Facebook API types handled in global.d.ts

interface FBPermissions {
  fields: string;
}

interface FBLoginOptions {
  scope: string;
}

interface FBShareParams {
  method: string;
  href: string;
}

// Facebook's own share dialog, used when the JS SDK never loaded. Ad blockers,
// corporate DNS and tracking-protection defaults all drop connect.facebook.net,
// which leaves window.FB undefined.
const FACEBOOK_SHARER_URL = "https://www.facebook.com/sharer/sharer.php?u=";

export const isFacebookShareSdkReady = (): boolean =>
  typeof window !== "undefined" && typeof window.FB?.ui === "function";

export default class FaceBookAPI {
  permissions: FBPermissions;
  loginOptions: FBLoginOptions;

  constructor() {
    this.permissions = {
      fields: "email, first_name, last_name, gender, picture",
    };
    this.loginOptions = {
      scope: "email public_profile", // Standard Facebook Login scopes (no commas)
    };
  }

  getFBProfile(): Promise<unknown> {
    return new Promise((resolve) => {
      // The Facebook SDK dispatches api() arguments by type at runtime
      // (function -> callback, object -> params) regardless of position, which
      // the simplified positional typing in globals.d.ts does not capture.
      (window.FB.api as (...args: unknown[]) => void)(
        "/me",
        (response: unknown) => {
          resolve(response);
        },
        this.permissions
      );
    });
  }

  loginWithFacebook(): Promise<unknown> {
    return new Promise((resolve) => {
      window.FB.login((response: unknown) => {
        resolve(response);
      }, this.loginOptions);
    });
  }

  loginStatus(): Promise<unknown> {
    return new Promise((resolve) => {
      window.FB.getLoginStatus((response: unknown) => {
        resolve(response);
      });
    });
  }

  expiresAt(expiresIn: number): number {
    return new Date().getTime() + expiresIn * 1000;
  }

  share(href: string): Promise<unknown> {
    // Without this guard the SDK-less case throws inside the executor, the
    // rejection is never caught by the click handler, and the share silently
    // does nothing while filing a TypeError on window.FB.ui.
    if (!isFacebookShareSdkReady()) {
      window.open(
        `${FACEBOOK_SHARER_URL}${encodeURIComponent(href)}`,
        "_blank",
        "noopener,noreferrer,width=600,height=500"
      );
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      window.FB.ui(
        {
          method: "share",
          href,
        } as FBShareParams,
        (response: unknown) => {
          resolve(response);
        }
      );
    });
  }
}
